import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { leadershipTeam, staffMembers } from '../data/mock';

const Team = () => {
  return (
    <div>
      <SEO 
        title="Our Team - Leadership & Staff"
        description="Meet the dedicated team behind Mentors in Motion. Our leadership and staff are committed to empowering St. Louis youth through mentoring and education."
        keywords="mentors in motion team, nonprofit leadership St. Louis, youth program staff, Alandon Pitts"
        canonicalUrl="/team"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Our Team
            </h1>
            <p className="text-base text-gray-300 leading-relaxed">
              Meet the passionate leaders and dedicated staff who make Mentors in Motion's mission possible. Our team brings expertise in education, youth development, creative arts, and community building, but most importantly, a deep commitment to our scholars. They design and deliver the mentoring, tutoring, and creative programs that help young people feel seen, supported, and capable, opening doors to better grades, stronger life skills, and a clearer vision for their future.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Leadership
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Our Leadership Team
            </h3>
          </div>

          <div className="space-y-12">
            {leadershipTeam.map((leader, index) => (
              <div
                key={leader.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative">
                    <div className={`absolute inset-0 ${index % 2 === 0 ? 'bg-red-600/10' : 'bg-gray-200'} rounded-3xl transform ${index % 2 === 0 ? 'rotate-3' : '-rotate-3'}`} />
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="relative rounded-3xl shadow-xl w-full max-w-md mx-auto object-cover object-top aspect-square"
                    />
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="inline-block bg-red-100 text-red-600 px-4 py-1 rounded-full text-sm font-medium mb-4">
                    {leader.title}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {leader.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {leader.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Staff Members */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Our Staff
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The Team Behind the Mission
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our dedicated staff members work directly with scholars every day, bringing our programs to life.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {staffMembers.map((staff) => (
              <Card key={staff.id} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
                <CardContent className="p-0">
                  <div className="relative overflow-hidden">
                    <img
                      src={staff.image}
                      alt={staff.name}
                      className={`w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300 ${staff.imagePosition === 'center' ? 'object-center' : 'object-top'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900">{staff.name}</h4>
                    <p className="text-sm text-gray-500">{staff.title}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Photo */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_0b676179-5cd1-44f5-bdb2-b528e519da7d/artifacts/zzs2znmm_alphabet-class-conceptual-cube-301926.jpg"
              alt="MiM team with students"
              className="w-full rounded-3xl shadow-2xl object-cover aspect-[21/9]"
            />
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-700 text-xl font-medium max-w-3xl mx-auto">
              Our team building relationships with scholars through hands-on, creative learning experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Want to Join Our Team?
          </h2>
          <p className="text-xl text-red-100 mb-10">
            We're always looking for passionate individuals who want to make a difference in the lives of young scholars.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8">
                Get Involved
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="mailto:info@mentorsinmotion.org">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8">
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
